/**
 * The intake Action Center — Calls and Form Entries.
 *
 * Same primitive as the Lead's, configured smaller, because these records have
 * a simpler life. A call or a form entry is something that arrived; the two
 * things a person does with it are annotate it and decide whether it is
 * business. So: a note, and — only while the decision is still open — Create
 * Lead and Link Lead.
 *
 * There is no follow-up date and no Verified here. Both belong to the Lead this
 * may become; putting them on an intake record would invent a second, parallel
 * lifecycle for something whose whole job is to stop being an intake record.
 *
 * Create Lead and Link Lead reuse `IntakeReviewDialog` exactly as the intake desk
 * does — this component owns none of that logic, it only decides when the two
 * buttons are worth offering.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link2, Plus, Trash2 } from 'lucide-react';
import { ActionCenter, Button } from '@command-center/ui-system';
import { IntakeReviewDialog, type IntakeMode } from '../views/IntakeReviewDialog';
import type { IntakeSourceState } from './intake-action-banner';
import { leadloopApi } from '../lib/leadloop-api';

export function IntakeActionCenter({
  recordId,
  sourceType,
  state,
  onAddNote,
}: {
  /** The call or form-submission id, used to reset the draft between records. */
  recordId: string;
  sourceType: 'call' | 'form_submission';
  state: IntakeSourceState;
  onAddNote: (body: string) => Promise<void>;
}) {
  const [note, setNote] = useState('');
  const [seededFor, setSeededFor] = useState(recordId);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [reviewMode, setReviewMode] = useState<IntakeMode | null>(null);

  if (seededFor !== recordId) {
    setSeededFor(recordId);
    setNote('');
    setError(null);
    setSaved(null);
  }

  const noun = sourceType === 'call' ? 'call' : 'form entry';
  const intake = state.intake;
  /*
    The three outcome actions appear only while they would do something: the intake
    still exists and is unresolved. Once it has become or been linked to a Lead,
    the banner above says so and links to it — offering "Create Lead" beside that
    sentence invites a duplicate.
  */
  const open = Boolean(intake && /^(unresolved|claiming)$/.test(intake.status)) && !state.lead;
  // AI eligibility is advisory in the review-first workflow. A human can still
  // create or link a Lead, and the dialog shows the classification as context.
  const leadEligible = open;

  const submit = async () => {
    const body = note.trim();
    if (!body) return;
    setPending(true);
    setError(null);
    try {
      await onAddNote(body);
      setNote('');
      setSaved('Saved');
      window.setTimeout(() => setSaved(null), 2500);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'This note could not be saved.');
    } finally {
      setPending(false);
    }
  };

  return (
    <>
      {/* Rendered outside the form so the portaled dialog cannot submit it. */}
      {intake && (
        <IntakeReviewDialog
          intake={intake}
          open={reviewMode !== null}
          onOpenChange={(next) => setReviewMode(next ? (reviewMode ?? 'details') : null)}
          mode={reviewMode ?? 'details'}
          onModeChange={setReviewMode}
        />
      )}
      <ActionCenter
        heading={sourceType === 'form_submission' ? 'Quick Actions' : 'Action needed'}
        note={note}
        onNoteChange={(value) => {
          setNote(value);
          setSaved(null);
        }}
        notePlaceholder={`Add a note about this ${noun}…`}
        noteLabel={`Note about this ${noun}`}
        noteRows={2}
        onSubmit={submit}
        disabled={!note.trim()}
        pending={pending}
        error={error}
        success={saved}
        actions={open && (
          <>
            {leadEligible && (
              <>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="gap-1.5"
                  onClick={() => setReviewMode('create')}
                >
                  <Plus className="h-3.5 w-3.5" aria-hidden />
                  Create Lead with AI
                </Button>
                {intake && (
                  <SuggestedLinkAction
                    intakeId={intake.id}
                    onLink={() => setReviewMode('link')}
                  />
                )}
              </>
            )}
            {/* Kept because "this is not business" is still an answer the
                record needs, and it is the only place left to give it. */}
            {intake && (
              <DismissIntakeButton
                intakeId={intake.id}
                note={note}
                onAddNote={onAddNote}
                onSuccess={() => {
                  setNote('');
                  setError(null);
                  setSaved('Dismissed');
                }}
                onError={setError}
              />
            )}
          </>
        )}
      />
    </>
  );
}

function SuggestedLinkAction({ intakeId, onLink }: { intakeId: string; onLink: () => void }) {
  const detail = useQuery({
    queryKey: ['intake', intakeId],
    queryFn: () => leadloopApi.intakeDetail(intakeId),
  });
  const suggestedLead = detail.data?.matches.leads[0];
  if (!suggestedLead) return null;
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
      Suggested: <span className="font-medium text-foreground">{suggestedLead.record.title}</span>
      <Button type="button" size="sm" variant="outline" className="gap-1.5" onClick={onLink}>
        <Link2 className="h-3.5 w-3.5" aria-hidden />
        Link Lead
      </Button>
    </span>
  );
}

function DismissIntakeButton({
  intakeId,
  note,
  onAddNote,
  onSuccess,
  onError,
}: {
  intakeId: string;
  note: string;
  onAddNote: (body: string) => Promise<void>;
  onSuccess: () => void;
  onError: (message: string) => void;
}) {
  const queryClient = useQueryClient();
  const dismiss = useMutation({
    mutationFn: async () => {
      const body = note.trim();
      // A typed Quick Note is part of the user's Dismiss action. Save it first;
      // if that write fails, do not remove the record and lose the note.
      if (body) await onAddNote(body);
      return leadloopApi.dismissIntake(intakeId, 'not_qualified', body || null);
    },
    onSuccess: async () => {
      onSuccess();
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['intake'] }),
        queryClient.invalidateQueries({ queryKey: ['intake-source'] }),
        queryClient.invalidateQueries({ queryKey: ['ai-systems', 'calls'] }),
        queryClient.invalidateQueries({ queryKey: ['cascade-form-submissions'] }),
      ]);
    },
    onError: (error) => onError(
      error instanceof Error ? error.message : 'The record could not be dismissed.',
    ),
  });
  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      className="gap-1.5"
      disabled={dismiss.isPending}
      onClick={() => dismiss.mutate()}
    >
      <Trash2 className="h-3.5 w-3.5" aria-hidden />
      {dismiss.isPending ? 'Dismissing…' : 'Dismiss'}
    </Button>
  );
}
